import { useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Box, CircularProgress, Divider, Grid, Paper, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import {
  SEGMENTS_MANAGER_ARIA,
  SEGMENTS_MANAGER_TEXT,
} from '~/modules/covenants/CovenantsSegmentsManager/constants.ts';
import {
  SUPERVISOR_ADMINISTRATION_ERRORS,
  SUPERVISOR_ADMINISTRATION_TEXT,
} from '~/modules/supervisor-administration/constants.ts';
import useCovenantsSegmentsManagerForm from '~/modules/covenants/CovenantsSegmentsManager/hooks/useCovenantsSegmentsManagerForm.ts';
import useSegmentsManagerActions from '~/modules/covenants/CovenantsSegmentsManager/hooks/useSegmentsManagerActions.ts';
import SupervisorAdministrationCard from '~/modules/supervisor-administration/SupervisorAdministrationCard.tsx';
import {
  buildSupervisorEmailFieldName,
  filterSupervisorSegments,
} from '~/modules/covenants/CovenantsSegmentsManager/utils.ts';
import { getCovenantsRequestErrorMessage } from '~/modules/covenants/utils.ts';
import {
  buildSupervisorAdministrationEmailFieldName,
  hasDuplicateSupervisorAdministrationEmail,
} from '~/modules/supervisor-administration/utils.ts';
import { useGuaranteesSupervisorSegmentsGroup } from '~/modules/supervisor-administration/useGuaranteesSupervisorSegmentsGroup.ts';
import { SearchPanel } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/Filters/SearchPanel.tsx';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { segmentsStyles } from '~/modules/covenants/CovenantsSegmentsManager/styles.ts';
import {
  selectSupervisorSegments,
  selectSupervisorSegmentsError,
  selectSupervisorSegmentsStatus,
} from '~/store/covenants/selectors.ts';
import { fetchSupervisorSegments } from '~/store/covenants/thunks/supervisorSegmentThunks.ts';
import { FetchStatus } from '~/store/covenants/types.ts';
import type { AppDispatch } from '~/store/store.ts';
import useGetUserRole from '~/hooks/useGetUserRole';
import { useFeatureFlags } from '~/helpers/FeatureFlagsContext.tsx';

const SegmentsManagerPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isManagerLogin } = useGetUserRole();
  const { isGuaranteesEnabled } = useFeatureFlags();
  const segments = useSelector(selectSupervisorSegments);
  const segmentsStatus = useSelector(selectSupervisorSegmentsStatus);
  const segmentsError = useSelector(selectSupervisorSegmentsError);
  const formMethods = useCovenantsSegmentsManagerForm(segments);

  const [modalErrorText, setModalErrorText] = useState('');
  const [hasLoadedSegmentsOnce, setHasLoadedSegmentsOnce] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [submittedSearchQuery, setSubmittedSearchQuery] = useState('');
  const { addingSegmentId, deletingSupervisorId, handleAddSupervisor, handleDeleteSupervisor } =
    useSegmentsManagerActions({
      dispatch,
      formMethods,
      segments,
      onError: setModalErrorText,
    });
  const visibleSegments = useMemo(
    () => filterSupervisorSegments(segments, submittedSearchQuery),
    [segments, submittedSearchQuery]
  );
  const {
    addingSectionId: addingGuaranteesSectionId,
    deletingUserId: deletingGuaranteesSupervisorId,
    errorMessage: guaranteesSegmentsErrorMessage,
    handleAddUser: addGuaranteesUser,
    handleDeleteUser: deleteGuaranteesUser,
    handleSearchSubmit: searchGuaranteesSupervisors,
    isLoading: isGuaranteesSegmentsLoading,
    sections: guaranteesSections,
  } = useGuaranteesSupervisorSegmentsGroup({
    groupKey: 'uakdo',
    enabled: isManagerLogin && isGuaranteesEnabled,
  });

  useEffect(() => {
    if (!isManagerLogin) {
      return;
    }

    void dispatch(fetchSupervisorSegments());
  }, [isManagerLogin, dispatch]);

  useEffect(() => {
    if (segmentsError?.message) {
      setModalErrorText(segmentsError.message);
    }
  }, [segmentsError]);

  useEffect(() => {
    if (guaranteesSegmentsErrorMessage) {
      setModalErrorText(guaranteesSegmentsErrorMessage);
    }
  }, [guaranteesSegmentsErrorMessage]);

  useEffect(() => {
    if (segmentsStatus === FetchStatus.Success || segmentsStatus === FetchStatus.Error) {
      setHasLoadedSegmentsOnce(true);
    }
  }, [segmentsStatus]);

  if (!isManagerLogin) {
    return null;
  }

  const isInitialLoading = !hasLoadedSegmentsOnce && segmentsStatus === FetchStatus.Loading;

  const handleSearchSubmit = (email: string) => {
    const nextSubmittedSearchQuery = email.trim();

    setSubmittedSearchQuery(nextSubmittedSearchQuery);

    if (isGuaranteesEnabled) {
      void searchGuaranteesSupervisors(nextSubmittedSearchQuery);
    }
  };

  const handleAddGuaranteesUser = async (sectionId: string) => {
    const fieldName = buildSupervisorAdministrationEmailFieldName(sectionId);
    const isFieldValid = await formMethods.trigger(fieldName);
    const email = formMethods.getValues(fieldName)?.trim() ?? '';
    const section = guaranteesSections.find((item) => item.id === sectionId);

    if (!email || !isFieldValid) {
      return;
    }

    if (hasDuplicateSupervisorAdministrationEmail(section?.users ?? [], email)) {
      formMethods.setError(fieldName, {
        type: 'duplicate',
        message: SUPERVISOR_ADMINISTRATION_ERRORS.duplicateEmail,
      });

      return;
    }

    try {
      await addGuaranteesUser(sectionId, email);
      formMethods.setValue(fieldName, '', {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
      formMethods.clearErrors(fieldName);
    } catch (error) {
      setModalErrorText(getCovenantsRequestErrorMessage(error));
    }
  };

  const handleDeleteGuaranteesUser = async (sectionId: string, userId: string) => {
    try {
      await deleteGuaranteesUser(sectionId, userId);
    } catch (error) {
      setModalErrorText(getCovenantsRequestErrorMessage(error));
    }
  };

  if (isInitialLoading) {
    return (
      <Box sx={commonStyles.centeredLoader}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <Box sx={commonStyles.pageRoot}>
        <Box sx={segmentsStyles.pageContent}>
          <Box sx={commonStyles.header}>
            <Box>
              <Typography variant="h4" sx={commonStyles.title}>
                {SEGMENTS_MANAGER_TEXT.pageTitle}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={commonStyles.subtitle}>
                {SEGMENTS_MANAGER_TEXT.pageSubtitle}
              </Typography>
            </Box>
          </Box>
          <Divider sx={segmentsStyles.pageDivider} />

          <Box sx={commonStyles.content}>
            <SearchPanel
              searchQuery={searchQuery}
              placeholder={SEGMENTS_MANAGER_TEXT.searchPlaceholder}
              searchButtonAriaLabel={SEGMENTS_MANAGER_ARIA.searchButton}
              clearButtonAriaLabel={SEGMENTS_MANAGER_ARIA.clearSearchButton}
              autoComplete="off"
              onSearchChange={(nextSearchQuery) => {
                setSearchQuery(nextSearchQuery);

                if (!nextSearchQuery.length) {
                  setSubmittedSearchQuery('');

                  if (isGuaranteesEnabled) {
                    void searchGuaranteesSupervisors('');
                  }
                }
              }}
              onSearchSubmit={() => {
                handleSearchSubmit(searchQuery);
              }}
            />

            {!!visibleSegments.length ? (
              <Grid container spacing={3}>
                {visibleSegments.map((segment) => (
                  <Grid key={segment.segment.id} size={{ xs: 12, md: 6, xl: 4 }}>
                    <SupervisorAdministrationCard
                      id={segment.segment.id}
                      title={segment.segment.displayName || segment.segment.id}
                      subtitleRows={[
                        `${SEGMENTS_MANAGER_TEXT.segmentCodePrefix}: ${segment.segment.id}`,
                        `${SEGMENTS_MANAGER_TEXT.supervisorsCountPrefix}: ${segment.supervisors.length}`,
                      ]}
                      users={segment.supervisors}
                      fieldName={buildSupervisorEmailFieldName(segment.segment.id)}
                      isAdding={addingSegmentId === segment.segment.id}
                      deletingUserId={deletingSupervisorId}
                      emptyText={SEGMENTS_MANAGER_TEXT.emptySupervisors}
                      onAddUser={handleAddSupervisor}
                      onDeleteUser={handleDeleteSupervisor}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Paper elevation={0} sx={segmentsStyles.emptyPaper}>
                <Typography variant="h6" gutterBottom>
                  {SEGMENTS_MANAGER_TEXT.emptyTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {SEGMENTS_MANAGER_TEXT.emptyDescription}
                </Typography>
              </Paper>
            )}

            {isGuaranteesEnabled && (
              <Box sx={segmentsStyles.guaranteesSection}>
                <Divider sx={segmentsStyles.pageDivider} />
                <Box sx={segmentsStyles.guaranteesSectionHeader}>
                  <Typography variant="h5" sx={commonStyles.title}>
                    {SUPERVISOR_ADMINISTRATION_TEXT.guaranteesSectionTitle}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={commonStyles.subtitle}
                  >
                    {SUPERVISOR_ADMINISTRATION_TEXT.guaranteesSectionSubtitle}
                  </Typography>
                </Box>

                {isGuaranteesSegmentsLoading ? (
                  <Box sx={commonStyles.centeredLoader}>
                    <CircularProgress />
                  </Box>
                ) : guaranteesSections.length ? (
                  <Grid container spacing={3}>
                    {guaranteesSections.map((section) => (
                      <Grid key={section.id} size={{ xs: 12, md: 6, xl: 4 }}>
                        <SupervisorAdministrationCard
                          id={section.id}
                          title={section.title}
                          subtitleRows={[
                            `${SUPERVISOR_ADMINISTRATION_TEXT.guaranteesRolePrefix}: ${section.title}`,
                            `${SUPERVISOR_ADMINISTRATION_TEXT.guaranteesUsersCountPrefix}: ${section.users.length}`,
                          ]}
                          users={section.users}
                          fieldName={buildSupervisorAdministrationEmailFieldName(section.id)}
                          isAdding={addingGuaranteesSectionId === section.id}
                          deletingUserId={deletingGuaranteesSupervisorId}
                          isLoading={isGuaranteesSegmentsLoading}
                          onAddUser={(sectionId) => {
                            void handleAddGuaranteesUser(sectionId);
                          }}
                          onDeleteUser={(sectionId, userId) => {
                            void handleDeleteGuaranteesUser(sectionId, userId);
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Paper elevation={0} sx={segmentsStyles.emptyPaper}>
                    <Typography variant="h6" gutterBottom>
                      {SUPERVISOR_ADMINISTRATION_TEXT.emptyTitle}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {SUPERVISOR_ADMINISTRATION_TEXT.emptyDescription}
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}
          </Box>
        </Box>

        <ModalAlert
          isOpen={!!modalErrorText}
          text={modalErrorText}
          onClose={() => setModalErrorText('')}
        />
      </Box>
    </FormProvider>
  );
};

export default SegmentsManagerPage;
